// src/api/promotion/promotionApi.ts
import { axiosInstance } from "src/api/axiosInstance";
import type { ApiResponse } from "src/api/common/apiTypes";

export type PromotionType = "EVENT" | "TIME_DEAL" | "FLASH_SALE" | "SEASONAL";
export type PromotionStatus = "DRAFT" | "ACTIVE" | "CANCELED";

export interface PromotionVolumePrice {
  productId: number;
  minQuantity: number;
  discountType: "FIXED" | "PERCENTAGE";
  discountValue: number;
  isActive: boolean;
}

export interface PromotionProduct {
  productId: number;
  variantId?: number | null;
  productName?: string;
  promotionProductName?: string;
  consumerPrice: number;
  promotionPrice: number;
  maxQuantityPerProduct: number;
  maxQuantityPerOrder: number;
  isActive?: boolean;
  displayOrder?: number;
  productOrder?: number;
}

export interface PromotionProductDetail extends PromotionProduct {
  promotionProductId: number;
  promotionId: number;
  productCode: string;
  productName: string;
  promotionProductName: string;
  variantName?: string;
  discountRate: number;
  discountAmount: number;
  soldQuantity: number;
}

export interface PromotionAddon {
  promotionProductAddonId?: number; // 수정 시 필요
  productId: number;
  addonMappingId: number;
  addonMappingName: string;
  originalAddonPrice: number;
  promotionAddonPrice: number;
  isActive?: boolean;
  displayOrder?: number;
}

export interface PromotionAddonDetail extends PromotionAddon {
  promotionProductAddonId: number;
  addonMappingName: string;
  originalAddonPrice: number;
}

export interface PromotionListItem {
  promotionId: number;
  promotionCode: string;
  promotionName: string;
  promotionType: PromotionType;
  promotionStatus: PromotionStatus;
  effectiveStatus: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  productCount: number;
  createdAt: string;
}

export interface PromotionDetail {
  promotionId: number;
  promotionCode: string;
  promotionName: string;
  promotionDescription: string;
  promotionType: PromotionType;
  promotionStatus: PromotionStatus;
  effectiveStatus: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  displayOrder: number;
  bannerImageUrl: string;
  maxQuantity: number;
  soldQuantity: number;
  promotionProducts: PromotionProductDetail[];
  promotionAddons: PromotionAddonDetail[];
  promotionVolumePrices?: PromotionVolumePrice[];
  createdAt: string;
  updatedAt: string;
}

export interface PromotionListParams {
  promotionType?: PromotionType | "";
  promotionStatus?: PromotionStatus | "";
  page: number;
  size: number;
}

export interface PromotionListResponse {
  statusCode: number;
  message: string;
  data: {
    content: PromotionListItem[];
    page: {
      number: number;
      size: number;
      totalElements: number;
      totalPages: number;
    };
  };
}

export interface PromotionCreatePayload {
  promotionName: string;
  promotionDescription: string;
  promotionType: PromotionType;
  promotionStatus: PromotionStatus;
  startDate: string;
  endDate: string;
  maxQuantity: number;
  promotionProducts: PromotionProduct[];
  promotionAddons: PromotionAddon[];
  promotionVolumePrices?: PromotionVolumePrice[];
}

export interface PromotionUpdatePayload {
  promotionName: string;
  promotionDescription: string;
  promotionType: PromotionType;
  promotionStatus: PromotionStatus;
  startDate: string;
  endDate: string;
  displayOrder: number;
  bannerImageUrl: string;
  maxQuantity: number;
  promotionProducts: PromotionProduct[];
  promotionAddons: PromotionAddon[];
  promotionVolumePrices?: PromotionVolumePrice[];
}

const BASE = "/v1/admin/baseinfo/promotion";

// 프로모션 목록 조회
export const getPromotionListApi = async (
  params: PromotionListParams
): Promise<PromotionListResponse> => {
  const res = await axiosInstance.get<PromotionListResponse>(`${BASE}/list`, {
    params: {
      promotionType: params.promotionType || undefined,
      promotionStatus: params.promotionStatus || undefined,
      page: params.page,
      size: params.size,
    },
  });
  return res.data;
};

// 프로모션 상세 조회
export const getPromotionDetailApi = async (id: number): Promise<ApiResponse<PromotionDetail>> => {
  const res = await axiosInstance.get<ApiResponse<PromotionDetail>>(`${BASE}/${id}`);
  return res.data;
};

// 프로모션 등록 (멀티파트 - 이미지 파일 포함)
export const createPromotionWithImageApi = async (
  payload: PromotionCreatePayload,
  bannerImageFile?: File | null
): Promise<ApiResponse<PromotionDetail>> => {
  const formData = new FormData();
  formData.append("data", new Blob([JSON.stringify(payload)], { type: "application/json" }));
  if (bannerImageFile) {
    formData.append("bannerImage", bannerImageFile);
  }

  const res = await axiosInstance.post<ApiResponse<PromotionDetail>>(BASE, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

// 프로모션 등록 (JSON - URL만)
// export const createPromotionApi = async (
//   payload: PromotionCreatePayload & { bannerImageUrl?: string }
// ): Promise<ApiResponse<PromotionDetail>> => {
//   const res = await axiosInstance.post<ApiResponse<PromotionDetail>>(BASE, payload);
//   return res.data;
// };

// 프로모션 수정 (멀티파트 - 이미지 파일 포함)
export const updatePromotionWithImageApi = async (
  id: number,
  payload: PromotionUpdatePayload,
  bannerImageFile?: File | null
): Promise<ApiResponse<PromotionDetail>> => {
  const formData = new FormData();
  formData.append("data", new Blob([JSON.stringify(payload)], { type: "application/json" }));
  if (bannerImageFile) {
    formData.append("bannerImage", bannerImageFile);
  }

  const res = await axiosInstance.put<ApiResponse<PromotionDetail>>(`${BASE}/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

// 프로모션 수정 (JSON - URL만)
// export const updatePromotionApi = async (
//   id: number,
//   payload: PromotionUpdatePayload
// ): Promise<ApiResponse<PromotionDetail>> => {
//   const res = await axiosInstance.put<ApiResponse<PromotionDetail>>(`${BASE}/${id}`, payload);
//   return res.data;
// };

// 프로모션 삭제
export const deletePromotionApi = async (id: number): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.delete<ApiResponse<null>>(`${BASE}/${id}`);
  return res.data;
};

// 프로모션 상품 삭제
export const deletePromotionProductApi = async (promotionProductId: number): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.delete<ApiResponse<null>>(`${BASE}/products/${promotionProductId}`);
  return res.data;
};

// 프로모션 상태 변경
export const updatePromotionStatusApi = async (
  id: number,
  status: PromotionStatus
): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.patch<ApiResponse<null>>(`${BASE}/${id}/status?status=${status}`);
  return res.data;
};
